<template>
    <v-card v-if="tree" outlined class="vq mb-4">
        <div class="vq-head">
            <div>
                <div class="vq-title">{{ $t('unit.vq.title') }}</div>
                <div class="vq-sub">{{ $t('unit.vq.stats', {
                    s: stops.length, q: subQuests.length }) }}</div>
            </div>
            <!-- ③ 按鈕文字反映狀態：停在既有停點上就是「加一題」，否則「新增停點」 -->
            <v-btn small outlined class="normal-case nowrap" @click="addHere">
                <v-icon small left>mdi-plus</v-icon>{{ addLabel }}
            </v-btn>
        </div>

        <div v-if="videoUrl" class="vq-player">
            <HlsPlayer ref="player" :src="videoUrl" @time="onTime" @duration="onDuration" />

            <div v-if="duration > 0" class="vq-track-wrap">
                <div ref="track" class="vq-track" @mousedown="onTrackDown">
                    <div class="vq-track-bar">
                        <div class="vq-track-played" :style="{ width: playedPercent + '%' }"></div>
                    </div>
                    <div v-for="(m, mi) in markers" :key="mi" class="vq-mk-wrap"
                        :style="{ left: m.x + 'px' }">
                        <!-- ⑬⑭ 太近的合併成群組，點了選要編哪一個 -->
                        <v-menu v-if="m.stops.length > 1" offset-y>
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="vq-mk vq-mk--group"
                                    v-bind="attrs" v-on="on">
                                    <v-icon x-small>mdi-dots-horizontal</v-icon>
                                    <span class="vq-mk-badge">{{ m.stops.length }}</span>
                                </button>
                            </template>
                            <v-list dense>
                                <v-subheader class="nowrap">{{ $t('unit.vq.group_pick', {
                                    n: m.stops.length }) }}</v-subheader>
                                <v-list-item v-for="s in m.stops" :key="s.time"
                                    @click="focusStop(s)">
                                    <v-list-item-title class="nowrap">
                                        {{ clock(s.time) }} · {{ $t('unit.vq.quest_n', {
                                            n: s.quests.length }) }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <!-- ⑨⑩⑪⑫ 單一停點：可拖曳、顯示編號、多題有角標、hover 展開膠囊 -->
                        <button v-else type="button" class="vq-mk"
                            :class="{ 'vq-mk--on': isStopActive(m.stops[0]),
                                      'vq-mk--drag': isDragging(m.stops[0]) }"
                            @mousedown.stop="startDrag($event, m.stops[0])"
                            @mouseenter="hoverTime = m.stops[0].time"
                            @mouseleave="hoverTime = null"
                            @click="focusStop(m.stops[0])">
                            <span v-if="isStopActive(m.stops[0])" class="vq-mk-cap nowrap">
                                {{ clock(m.stops[0].time) }} ·
                                {{ $t('unit.vq.quest_n', { n: m.stops[0].quests.length }) }}
                            </span>
                            <template v-else>
                                <span>{{ stopNumber(m.stops[0]) }}</span>
                                <span v-if="m.stops[0].quests.length > 1" class="vq-mk-badge">
                                    {{ m.stops[0].quests.length }}
                                </span>
                            </template>
                        </button>
                    </div>
                </div>
                <!-- ⑯ 左側操作說明、右側統計 -->
                <div class="vq-track-foot">
                    <span class="nowrap">{{ $t('unit.vq.track_help') }}</span>
                    <span class="nowrap">{{ $t('unit.vq.stats', {
                        s: stops.length, q: subQuests.length }) }}</span>
                </div>
            </div>
        </div>
        <v-alert v-else-if="youtubeBlocked" dense text type="warning" class="vq-note">
            {{ $t('unit.vq.youtube_blocked') }}
        </v-alert>
        <v-alert v-else dense text type="info" class="vq-note">
            {{ $t('unit.vq.no_video') }}
        </v-alert>

        <!-- 停點清單。⑧ 永遠依時間排序，「播完後」最後 -->
        <v-card v-for="stop in stops" :key="stop.time" outlined class="vqs"
            :class="{ 'vqs--on': isStopActive(stop) }"
            @mouseenter="hoverTime = stop.time" @mouseleave="hoverTime = null">
            <!-- 整列可點就展開。⚠ 列上的互動元素都要 `.stop`，
                 否則點時間膠囊或 ⋯ 會順便把卡片收起來。 -->
            <div class="vqs-head" @click="toggleStop(stop)">
                <span class="vqs-no">{{ stopNumber(stop) }}</span>

                <!-- ⑰⑱ 時間膠囊本身可編輯；點一下跳到該時刻並暫停 -->
                <input v-if="editingTime === stop.time" ref="timeInput" v-model="timeText"
                    class="vq-cap vq-cap--edit" type="text" @click.stop
                    @keydown.up.prevent="nudgeTime(1)" @keydown.down.prevent="nudgeTime(-1)"
                    @keydown.enter.prevent="commitTime(stop)"
                    @keydown.esc.prevent="editingTime = null" @blur="commitTime(stop)" />
                <button v-else type="button" class="vq-cap nowrap" @click.stop="tapTime(stop)">
                    {{ stop.time === TIME_END ? $t('unit.vq.time_end') : clock(stop.time) }}
                </button>

                <span class="vqs-meta nowrap">{{ $t('unit.vq.stop_quests', {
                    n: stop.quests.length }) }}</span>
                <!-- ㉓ 相距太近的提醒 -->
                <span v-if="tooClose.get(stop.time)" class="vqs-warn nowrap">
                    {{ $t('unit.vq.too_close', { n: tooClose.get(stop.time) }) }}
                </span>
                <v-spacer></v-spacer>

                <!-- ⑳㉒ 停點的 ⋯ 選單 -->
                <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop>
                            <v-icon small>mdi-dots-horizontal</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item v-if="stop.time === TIME_END" :disabled="!videoUrl"
                            @click="setStopTime(stop, currentTime)">
                            <v-list-item-title class="nowrap">
                                {{ $t('unit.vq.menu_play_at', { t: clock(currentTime) }) }}
                            </v-list-item-title>
                        </v-list-item>
                        <v-list-item v-else @click="setStopTime(stop, TIME_END)">
                            <v-list-item-title class="nowrap">
                                {{ $t('unit.vq.menu_at_end') }}
                            </v-list-item-title>
                        </v-list-item>
                        <v-list-item :disabled="stop.quests.length >= MAX_QUEST_PER_STOP"
                            @click="addQuestToStop(stop)">
                            <v-list-item-title class="nowrap">
                                {{ $t('unit.vq.menu_add_quest') }}
                            </v-list-item-title>
                        </v-list-item>
                        <v-list-item :disabled="stopLocked(stop)" @click="removeStop(stop)">
                            <v-list-item-title class="nowrap">
                                {{ stopLocked(stop) ? $t('unit.vq.stop_locked')
                                    : $t('unit.vq.menu_delete_stop', { n: stop.quests.length }) }}
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <!-- ⚠ 要 `.stop`：不然點它會觸發兩次（自己一次、冒泡到整列一次），
                     等於展開後立刻收起 -->
                <v-btn icon x-small @click.stop="toggleStop(stop)">
                    <v-icon small>
                        {{ expandedTime === stop.time ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                </v-btn>
            </div>

            <div v-if="expandedTime === stop.time" class="vqs-body">
                <div class="vqs-hint nowrap">{{ $t('unit.vq.stop_hint', {
                    n: stop.quests.length }) }}</div>

                <!-- ⑦ 作答順序 -->
                <div class="vqs-order-title nowrap">{{ $t('unit.vq.order_title') }}</div>
                <div v-for="(q, qi) in stop.quests" :key="questNumber(q)" class="vqq">
                    <div class="vqq-row">
                        <span class="vqq-no">{{ questNumber(q) }}</span>
                        <span class="vqq-summary" :title="summaryOf(q)">{{ summaryOf(q) }}</span>
                        <!-- ㉕ 摘要：選項數 · 正解數 -->
                        <span class="vqq-meta">
                            {{ correctCount(q) === 0
                                ? $t('unit.vq.q_no_correct')
                                : $t('unit.vq.q_meta', {
                                    opts: (q.options || []).length, correct: correctCount(q) }) }}
                        </span>
                        <!-- 規格 18（2026-09-21 依需求方調整）：
                             這顆就是「依序試聽這一題」，不是只播題目音。
                             ⚠ 原本在展開區左下角那顆已移除，功能合併到這裡。 -->
                        <AudioPlayBtn :state="sequenceState(q)" stop-icon
                            :playing="seqQuest === q"
                            :progress="playProgress"
                            :label="$t('unit.vq.audio.seq_play')"
                            @click="toggleSequence(q)" />
                        <!-- 規格 19／20：音檔狀態小標，依嚴重度取代顯示 -->
                        <span v-if="questAudioSummary(q)" class="vqa-tag nowrap"
                            :class="`vqa-tag--${questAudioSummary(q).level}`">
                            {{ $t(`unit.vq.audio.tag_${questAudioSummary(q).level}`, {
                                n: questAudioSummary(q).done,
                                total: questAudioSummary(q).total }) }}
                        </span>
                        <v-btn icon x-small :disabled="qi === 0"
                            :title="$t('unit.vq.move_up')" @click="moveQuest(stop, qi, -1)">
                            <v-icon small>mdi-arrow-up</v-icon>
                        </v-btn>
                        <v-btn icon x-small :disabled="qi === stop.quests.length - 1"
                            :title="$t('unit.vq.move_down')" @click="moveQuest(stop, qi, 1)">
                            <v-icon small>mdi-arrow-down</v-icon>
                        </v-btn>
                        <v-btn x-small text class="normal-case nowrap" @click="toggleQuest(q)">
                            {{ expandedQuest === q ? $t('unit.vq.collapse')
                                : $t('unit.vq.edit') }}
                        </v-btn>
                        <!-- 合成日記要用的小題不能刪：v-btn disabled 時收不到滑鼠事件，
                             title 放在外層 span 才看得到原因 -->
                        <span v-if="isLocked(q)" :title="$t('unit.vq.quest_locked')">
                            <v-btn icon x-small disabled>
                                <v-icon small>mdi-lock-outline</v-icon>
                            </v-btn>
                        </span>
                        <v-btn v-else icon x-small :title="$t('unit.vq.remove_quest')"
                            @click="removeQuest(q)">
                            <v-icon small>mdi-close</v-icon>
                        </v-btn>
                    </div>

                    <!-- ㉔ 原地展開，一次一題 -->
                    <div v-if="expandedQuest === q" class="vqq-edit">
                        <div class="vqc-q">
                            <div class="vqc-q-text">
                                <!-- 規格 1：貼著它所朗讀的那段文字 -->
                                <div class="vqa-row">
                                    <v-text-field v-model="q.quest_title.title" dense outlined
                                        hide-details="auto"
                                        :label="q.quest_title.title
                                            ? '' : $t('unit.vq.sub_title')"
                                        :counter="counterFor(q.quest_title.title)"
                                        :maxlength="MAX_TEXT"></v-text-field>
                                    <AudioPlayBtn :state="audioStateOf(q, 'title', null)"
                                        :playing="playingKey === audioKey(q, 'title', null)"
                                        :progress="playProgress"
                                        @click="onAudioClick(q, 'title', null)" />
                                </div>
                            </div>
                            <!-- ㉖㉗㉘ 題目背景圖獨立成欄 -->
                            <div class="vqc-cover">
                                <div class="vqc-field-label nowrap">{{ $t('unit.vq.cover') }}</div>
                                <div class="vqc-thumbwrap">
                                    <div class="vqc-thumb vqc-thumb--64"
                                        :class="{ 'vqc-thumb--empty': !mediaOf(q.cover_media_id) }">
                                        <img v-if="urlOf(q, 'cover_media_id')"
                                            :src="urlOf(q, 'cover_media_id')" alt="" />
                                        <span v-else class="vqc-noimg nowrap">
                                            {{ $t('unit.vq.no_img') }}
                                        </span>
                                    </div>
                                    <div class="vqc-thumb-side">
                                        <v-btn x-small outlined class="normal-case nowrap"
                                            @click="openPicker({ quest: q })">
                                            {{ $t('unit.vq.replace') }}
                                        </v-btn>
                                        <v-menu offset-y>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon x-small v-bind="attrs" v-on="on">
                                                    <v-icon small>mdi-dots-horizontal</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list dense>
                                                <v-list-item
                                                    :disabled="!mediaOf(q.cover_media_id)"
                                                    @click="clearCover(q)">
                                                    <v-list-item-title class="nowrap">
                                                        {{ $t('unit.vq.clear') }}
                                                    </v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </div>
                                </div>
                                <div v-if="mediaOf(q.cover_media_id)" class="vqc-fname"
                                    :title="mediaOf(q.cover_media_id).title">
                                    {{ mediaOf(q.cover_media_id).title }}
                                </div>
                            </div>
                        </div>

                        <!-- ㉛ 選項數與正解數 -->
                        <div class="vqc-optbar">
                            <span class="nowrap">{{ $t('unit.vq.opt_of', {
                                n: (q.options || []).length, max: MAX_OPTION }) }}</span>
                            <span :class="{ 'vqc-correct-n': correctCount(q) > 0 }">
                                {{ correctCount(q) === 0
                                    ? $t('unit.vq.need_correct')
                                    : $t('unit.vq.correct_n', { n: correctCount(q) }) }}
                            </span>
                        </div>

                        <!-- ㉙㉚㉜㉝ 選項是等高單列卡片 -->
                        <div v-for="(opt, j) in (q.options || [])" :key="opt.id" class="vqo"
                            :class="{ 'vqo--playing':
                                playingKey === audioKey(q, 'option', opt)
                                || playingKey === audioKey(q, 'response', opt) }">
                            <label class="vqo-correct nowrap"
                                :class="{ 'vqo-correct--on': isCorrect(q, opt) }">
                                <input type="checkbox" :checked="isCorrect(q, opt)"
                                    @change="setCorrect(q, opt, $event.target.checked)" />
                                <span>{{ $t('unit.vq.correct') }}</span>
                            </label>
                            <!-- 文字在前、圖在後 -->
                            <div class="vqo-fields">
                                <!-- ⚠ 有內容就把 label 拿掉：outlined 欄位的浮動標籤
                                     在填完之後只是佔位，兩欄都這樣處理 -->
                                <div class="vqa-row">
                                    <v-text-field v-model="opt.title" dense outlined
                                        hide-details="auto"
                                        :label="opt.title ? '' : $t('unit.vq.opt_title')"
                                        :counter="counterFor(opt.title)" :maxlength="MAX_TEXT">
                                    </v-text-field>
                                    <AudioPlayBtn :state="audioStateOf(q, 'option', opt)"
                                        :playing="playingKey === audioKey(q, 'option', opt)"
                                        :progress="playProgress"
                                        @click="onAudioClick(q, 'option', opt)" />
                                </div>
                                <v-btn v-if="!showResponse(q, opt)" x-small text
                                    class="normal-case vqo-addresp nowrap"
                                    @click="openResponse(q, opt)">
                                    <v-icon x-small left>mdi-plus</v-icon>
                                    {{ $t('unit.vq.add_response') }}
                                </v-btn>
                                <!-- 回饋內縮，內縮出來的空白處就是它的標籤 -->
                                <div v-else class="vqo-resp-row">
                                    <span class="vqo-resp-label nowrap">
                                        {{ $t('unit.vq.response_short') }}
                                    </span>
                                    <v-text-field :value="responseOf(q, opt)" dense outlined
                                        hide-details="auto" class="vqo-resp"
                                        :label="responseOf(q, opt)
                                            ? '' : $t('unit.vq.opt_response')"
                                        :counter="counterFor(responseOf(q, opt))"
                                        :maxlength="MAX_TEXT"
                                        @input="setResponse(q, opt, $event)"></v-text-field>
                                    <AudioPlayBtn :state="audioStateOf(q, 'response', opt)"
                                        :playing="playingKey === audioKey(q, 'response', opt)"
                                        :progress="playProgress"
                                        @click="onAudioClick(q, 'response', opt)" />
                                </div>
                            </div>
                            <div class="vqo-thumbwrap">
                                <div class="vqc-thumb vqc-thumb--48 vqc-thumb--round"
                                    :class="{ 'vqc-thumb--empty': !mediaOf(opt.img_media_id) }">
                                    <img v-if="urlOf(opt, 'img_media_id')"
                                        :src="urlOf(opt, 'img_media_id')" alt="" />
                                    <span v-else class="vqc-noimg nowrap">
                                        {{ $t('unit.vq.no_img') }}
                                    </span>
                                </div>
                                <v-menu offset-y>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn icon x-small class="vqo-dots"
                                            v-bind="attrs" v-on="on">
                                            <v-icon x-small>mdi-dots-horizontal</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense>
                                        <v-list-item @click="openPicker({ quest: q, option: j })">
                                            <v-list-item-title class="nowrap">
                                                {{ $t('unit.vq.replace') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                        <v-list-item :disabled="!mediaOf(opt.img_media_id)"
                                            @click="clearOptionImg(q, j)">
                                            <v-list-item-title class="nowrap">
                                                {{ $t('unit.vq.clear') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </div>
                            <v-btn icon x-small :title="$t('unit.vq.remove_opt')"
                                @click="removeOption(q, j)">
                                <v-icon small>mdi-close</v-icon>
                            </v-btn>
                        </div>

                        <div class="vqq-foot">
                            <v-btn x-small text class="normal-case nowrap"
                                :disabled="(q.options || []).length >= MAX_OPTION"
                                @click="addOption(q)">
                                <v-icon x-small left>mdi-plus</v-icon>{{ $t('unit.vq.add_opt') }}
                            </v-btn>
                        </div>
                    </div>
                </div>

                <!-- 在這個停點加一題。⚠ ⋯ 選單裡也有同一個動作（第 22 點），
                     但那是收合狀態下的入口；展開時老師要看得到按鈕。 -->
                <div class="vqs-add">
                    <v-btn x-small text class="normal-case nowrap"
                        :disabled="stop.quests.length >= MAX_QUEST_PER_STOP"
                        @click="addQuestToStop(stop)">
                        <v-icon x-small left>mdi-plus</v-icon>
                        {{ $t('unit.vq.menu_add_quest') }}
                    </v-btn>
                    <span v-if="stop.quests.length >= MAX_QUEST_PER_STOP"
                        class="vqs-full nowrap">
                        {{ $t('unit.vq.stop_full', { n: MAX_QUEST_PER_STOP }) }}
                    </span>
                </div>
            </div>
        </v-card>

        <!-- ⚠ `:ratio` 傳的是**這一格將來會被畫成的比例**，不是圖片本身的（見 VF9）：
             小題背景圖在學生端是滿版的寬背景、選項圖是圓形的 1:1。 -->
        <MediaPicker v-if="pickerOpen" :show.sync="pickerOpen" purpose="image"
            :ratio="pickerRatio" :value="pickerCurrentId" @select="onPicked" />
    </v-card>
</template>

<script>
import AudioPlayBtn from '@/components/console/diary/unit/AudioPlayBtn';
import HlsPlayer from '@/components/console/diary/unit/HlsPlayer';
import MediaPicker from '@/components/console/diary/media/MediaPicker';
import {
    TIME_END, MAX_QUEST_PER_STOP, groupStops, numberQuests, buildMarkers,
    tooCloseStops, clock, parseClock,
    AUDIO, audioState, audioSummary, playOrder, slotKey, slotText, slotAudioId, isQueueable,
} from '@/components/console/diary/unit/videoQuestUtils';

/// 文字欄位的上限。⚠ 真正的天花板是 `resources` 的 TEXT（65,535 bytes），
/// 後端 `writeVideoQuest` 會擋；這裡只是不讓單一欄位失控。
const MAX_TEXT = 200;
/// 一個小題最多幾個選項（需求方 2026-09-14）。後端 `MAX_OPTION` 也是 5。
const MAX_OPTION = 5;
/// ㉞ 字數計數器只在接近上限時出現，不要每欄常駐「2 / 200」
const COUNTER_AT = 0.8;
/// 拖曳超過這個像素才算拖，否則當成點擊
const DRAG_SLOP = 3;

/**
 * 互動影片問答的編輯器（VideoQuest.console.steps.md 的 VF1–VF5）。
 *
 * ⚠⚠ **「停點」是學生端既有行為的名字，不是新模型**——見 `videoQuestUtils.js`。
 *
 * ⚠ **語音沒有選擇入口。** 語音是系統依老師打的文字生成的（V8）。
 * ⚠ **只開放單選題型（`sa`）。** 其餘由我們手動維護（規格決策 #4）。
 * ⚠ **不區分單選／複選**（需求方 2026-09-14）：勾幾個正解都可以，至少一個。
 *
 * ⚠⚠ **就地修改 `tree` 這個 prop 的內容，不發事件。** 父層的 `isDirty` 是
 * `JSON.stringify(buildPayload())` 的比對，`JSON.stringify` 會遞迴讀過每個屬性，
 * 於是它們全都成為那個 computed 的相依。
 * **但新增屬性一定要 `$set`、刪除要 `$delete`**——Vue 2 對直接指派不建立相依，
 * 症狀是「改了但按儲存沒送出去」。
 */
export default {
    name: 'VideoQuestView',
    components: { AudioPlayBtn, HlsPlayer, MediaPicker },
    props: {
        tree: { type: Object, default: null },
        mediaMap: { type: Object, default: () => ({}) },
        videoMediaId: { type: Number, default: null },
        /// 舊的手工影片題目的主影片網址（`resources.video_link`）。後端只在沒有
        /// `video_media_id` 時給值，所以有素材的題目一律以素材為準。
        legacyVideoUrl: { type: String, default: '' },
        /// 不能刪的小題種類（合成日記要用的，見 questModeUtils.composeLockedTypes）
        lockedTypes: { type: Array, default: () => [] },
    },
    data() {
        return {
            TIME_END,
            MAX_TEXT,
            MAX_OPTION,
            MAX_QUEST_PER_STOP,
            currentTime: 0,
            duration: 0,
            trackWidth: 0,
            /// 展開中的停點時間（null = 全部收合）
            expandedTime: null,
            /// ㉔ 展開中的小題物件，一次只有一個
            expandedQuest: null,
            /// ⑮ 清單與時間軸雙向連動
            hoverTime: null,
            /// ⑰ 正在就地編輯時間的停點
            editingTime: null,
            timeText: '',
            pickerOpen: false,
            pickerTarget: null,
            responseOpen: {},
            AUDIO,
            /**
             * VF8 規格 12／13：每一句在「載入或上次存檔時」的文字。
             * 與現在輸入框裡的值不同 → 那一句是「待更新」。
             *
             * ⚠⚠ **這是純前端狀態，重新整理就會回到「沒有待更新」。**
             * 後端要等存檔、比對 `tts_text_hash` 才知道文字變了，幫不上忙。
             * 邏輯是一致的（沒存的修改本來就不見了），但老師要知道這件事。
             */
            audioBaseline: {},
            /// 規格 5：**整個編輯器只有一個** Audio 實例。⚠ 不要每顆鍵各自 new。
            player: null,
            /// 現在在播哪一句（`slotKey`）。null = 沒在播。
            playingKey: null,
            /// 0–1，規格 3 的進度環。
            playProgress: 0,
            /// 規格 15–17：依序試聽的佇列與位置。
            seqKeys: [],
            seqOn: false,
            /// ⚠ 哪一題正在依序試聽。只有 `seqOn` 的話，同一個停點下的每一列
            /// 都會同時顯示「播放中」——它們共用那個布林值。
            seqQuest: null,
        };
    },
    computed: {
        subQuests() {
            return (this.tree && Array.isArray(this.tree.quests)) ? this.tree.quests : [];
        },
        stops() {
            return groupStops(this.subQuests);
        },
        timedStops() {
            return this.stops.filter((s) => s.time !== TIME_END);
        },
        questNumbers() {
            return numberQuests(this.stops);
        },
        markers() {
            return buildMarkers(this.timedStops, this.trackWidth, this.duration);
        },
        tooClose() {
            return tooCloseStops(this.stops);
        },
        mainVideo() {
            return this.videoMediaId ? (this.mediaMap[String(this.videoMediaId)] || null) : null;
        },
        /**
         * ⚠⚠ YouTube 的影片不能用（需求方 2026-09-14）：console 播不了、沒有
         * `duration_ms`、隨時可能被下架。後端 `assertMainVideoPlayable` 也會擋。
         */
        youtubeBlocked() {
            const attrs = (this.mainVideo && this.mainVideo.attributes) || null;
            return !!(attrs && attrs.provider === 'youtube');
        },
        videoUrl() {
            if (!this.mainVideo) {
                /// ⚠ 舊題目沒有模式設定區，老師沒辦法自己選影片——不播它就完全不能標時間
                return this.legacyVideoUrl;
            }
            return this.youtubeBlocked ? '' : (this.mainVideo.url || '');
        },
        playedPercent() {
            return this.duration > 0
                ? Math.min(100, (this.currentTime / this.duration) * 100) : 0;
        },
        /** 目前這一秒上有沒有停點。⚠ 一律整秒比對（需求方 2026-09-14）。 */
        stopAtCursor() {
            return this.timedStops.find((s) => s.time === this.currentTime) || null;
        },
        /// ③ 按鈕文字反映狀態
        addLabel() {
            if (!this.videoUrl) {
                return this.$t('unit.vq.add_end_stop');
            }
            return this.stopAtCursor
                ? this.$t('unit.vq.add_at_stop', { t: clock(this.currentTime) })
                : this.$t('unit.vq.add_new_stop', { t: clock(this.currentTime) });
        },
        pickerCurrentId() {
            const t = this.pickerTarget;
            if (!t || !t.quest) {
                return null;
            }
            return t.option === undefined
                ? (t.quest.cover_media_id || null)
                : ((t.quest.options[t.option] || {}).img_media_id || null);
        },
        /**
         * 挑圖對話框要用的比例。⚠ 與 `.vqc-thumb--64` / `--48` 是**同一組數字**，
         * 改了一邊要改另一邊——縮圖與挑圖看到的形狀不一樣就失去意義了。
         *
         * ⚠ `pickerTarget.option` 是索引，`0` 是合法值，所以要用 `!= null` 判斷，
         * 不能用 truthy——第一個選項會被判成「不是選項」。
         */
        pickerRatio() {
            const target = this.pickerTarget;
            if (target && target.option != null) {
                /// 選項圖：學生端 `h-20 w-20 rounded-full`，正方形。
                return 1;
            }
            /// 小題背景圖：實際素材是 16:9（1080×608、1920×1080）。
            return 16 / 9;
        },
        /**
         * 規格 21 的相依來源。⚠ 只是讓 watcher 有東西可以 deep 追，值本身不用。
         * 直接 deep watch `tree` 也可以，但那會連 `time`、圖片 id 一起追，
         * 打一次字就重算好幾輪。
         */
        audioPendingSource() {
            return [this.audioBaseline, this.mediaMap,
                ((this.tree && this.tree.quests) || []).map((q) => playOrder(q)
                    .map(({ kind, opt }) => slotText(q, kind, opt)).join('\u0001')).join('\u0002')];
        },
    },
    watch: {
        /**
         * 規格 21：文字或素材狀態一變就重新回報。
         *
         * ⚠ **這個要 deep**，與下面的 `tree()` 相反：這裡要的正是「每打一個字都重算」。
         * ⚠ `immediate` 讓打開頁面就報一次，否則沒動過的題目鍵上不會顯示正確文字。
         */
        audioPendingSource: { handler: 'reportAudioPending', deep: true, immediate: true },
        /**
         * 換了一棵樹（打開另一題、或存檔後重新載入）就重建基準。
         *
         * ⚠ **不能用 deep**：deep watcher 會在老師每打一個字時觸發，
         * 基準跟著文字一起動的話，「待更新」永遠不會亮。
         */
        tree() {
            this.stopAudio();
            this.snapshotAudio();
        },
    },
    created() {
        /// ⚠ 不放進 `data`：不需要反應式，放進去會被 `JSON.stringify` 帶進存檔的 payload
        this.optionWatermarks = new WeakMap();
        this.snapshotAudio();
    },
    mounted() {
        this.measureTrack();
        /// ⑬ 合併門檻是**像素**距離，所以寬度一變就要重算
        if (typeof ResizeObserver !== 'undefined') {
            this.ro = new ResizeObserver(() => this.measureTrack());
            if (this.$refs.track) {
                this.ro.observe(this.$refs.track);
            }
        }
        window.addEventListener('resize', this.measureTrack);
    },
    updated() {
        /// 影片載入後軌道才出現，那時要再量一次
        if (this.$refs.track && this.ro && !this.roAttached) {
            this.ro.observe(this.$refs.track);
            this.roAttached = true;
            this.measureTrack();
        }
    },
    beforeDestroy() {
        if (this.ro) {
            this.ro.disconnect();
        }
        window.removeEventListener('resize', this.measureTrack);
        this.stopDrag();
        /// ⚠⚠ 一定要停：Audio 不是 DOM 節點，元件沒了它還會繼續響。
        this.stopAudio();
    },
    methods: {
        clock,
        isQueueable,

        /* ---------- VF8 音檔狀態 ---------- */

        /**
         * 把現在樹上每一句的文字記成基準（規格 12／13 的比較對象）。
         * 載入時與存檔後各叫一次。
         */
        snapshotAudio() {
            const next = {};
            for (const q of ((this.tree && this.tree.quests) || [])) {
                const no = this.questNumber(q);
                for (const { kind, opt } of playOrder(q)) {
                    next[slotKey(no, kind, opt)] = slotText(q, kind, opt);
                }
            }
            this.audioBaseline = next;
        },

        audioKey(q, kind, opt) {
            return slotKey(this.questNumber(q), kind, opt);
        },

        /** 一句現在的狀態。⚠ 三個參數要跟 `playOrder` 給的一致。 */
        audioStateOf(q, kind, opt) {
            const key = this.audioKey(q, kind, opt);
            return audioState(
                slotText(q, kind, opt),
                this.audioBaseline[key],
                this.mediaOf(slotAudioId(q, kind, opt)),
            );
        },

        /** 這一題每一句的狀態，供小標（規格 19）與依序試聽用。 */
        questAudioStates(q) {
            return playOrder(q).map(({ kind, opt }) => this.audioStateOf(q, kind, opt));
        },

        /** 規格 19：小題收合列的狀態小標。null = 不顯示。 */
        questAudioSummary(q) {
            return audioSummary(this.questAudioStates(q));
        },

        /* ---------- 播放（規格 3–6） ---------- */

        /**
         * 點一顆播放鍵。
         *
         * ⚠ 規格 10：失敗的那一顆點下去是「重試」，而重試的做法是**再存一次檔**
         * （後端的 `usable` 把 `failed` 排除在外，存檔就會重排）。
         * 這裡只往上丟事件，由外層決定要不要提示老師存檔。
         */
        onAudioClick(q, kind, opt) {
            const state = this.audioStateOf(q, kind, opt);
            if (state === AUDIO.FAILED) {
                this.$emit('audio-retry');
                return;
            }
            const key = this.audioKey(q, kind, opt);
            if (this.playingKey === key) {
                this.stopAudio();
                return;
            }
            /// ⚠ 手動點播會中斷依序試聽，否則兩邊會搶同一個 player。
            this.seqOn = false;
            this.seqKeys = [];
            this.seqQuest = null;
            this.playAudio(key, slotAudioId(q, kind, opt));
        },

        /**
         * 播一句。規格 5：**先停掉前一個**。
         *
         * ⚠ 每次都 `new Audio`（而不是換 `src` 重用）：換 src 之後舊的 `timeupdate`
         * 可能還在飛，進度環會跳一下。反正只有一個實例活著。
         */
        playAudio(key, mediaId) {
            this.stopAudio();
            const media = this.mediaOf(mediaId);
            if (!media || !media.url) {
                return;
            }
            const audio = new Audio(media.url);
            this.player = audio;
            this.playingKey = key;
            this.playProgress = 0;
            audio.addEventListener('timeupdate', () => {
                /// ⚠ `duration` 在 metadata 載入前是 NaN，直接除會讓環閃一下。
                const total = audio.duration;
                this.playProgress = total > 0 ? Math.min(1, audio.currentTime / total) : 0;
            });
            /// 規格 4：播完自動回到 ▶。
            audio.addEventListener('ended', () => this.onAudioEnded());
            /// ⚠ 播不出來也要收尾，否則那顆鍵會永遠停在「播放中」。
            audio.addEventListener('error', () => this.onAudioEnded());
            const played = audio.play();
            if (played && played.catch) {
                played.catch(() => this.onAudioEnded());
            }
        },

        stopAudio() {
            if (this.player) {
                this.player.pause();
                /// ⚠ 要解掉 src 並 load()，不然 Safari 會繼續下載整個檔案。
                this.player.removeAttribute('src');
                this.player.load();
            }
            this.player = null;
            this.playingKey = null;
            this.playProgress = 0;
        },

        /** 一句播完。依序試聽中就接下一句，否則就結束。 */
        onAudioEnded() {
            const wasSeq = this.seqOn;
            this.stopAudio();
            if (wasSeq) {
                this.seqNext();
            }
        },

        /* ---------- 依序試聽（規格 15–17） ---------- */

        /**
         * 規格 15／16：整題依學生聽到的順序播，播放中可隨時中斷。
         * 規格 17：⚠ **只排入可播放與待更新的**，產生中與失敗的跳過。
         */
        toggleSequence(q) {
            /// ⚠ 點的是「正在播的那一題」才是停止；點別題是**改播那一題**。
            if (this.seqOn && this.seqQuest === q) {
                this.stopSequence();
                return;
            }
            this.stopSequence();
            const queue = [];
            for (const { kind, opt } of playOrder(q)) {
                if (isQueueable(this.audioStateOf(q, kind, opt))) {
                    queue.push({ key: this.audioKey(q, kind, opt), id: slotAudioId(q, kind, opt) });
                }
            }
            if (!queue.length) {
                return;
            }
            this.seqKeys = queue;
            this.seqOn = true;
            this.seqQuest = q;
            this.seqNext();
        },

        stopSequence() {
            this.seqOn = false;
            this.seqKeys = [];
            this.seqQuest = null;
            this.stopAudio();
        },

        seqNext() {
            if (!this.seqOn) {
                return;
            }
            const next = this.seqKeys.shift();
            if (!next) {
                this.seqOn = false;
                this.seqQuest = null;
                return;
            }
            this.playAudio(next.key, next.id);
        },

        /** 規格 17：一句都排不進去時，整顆「依序試聽」要停用。 */
        canSequence(q) {
            return this.questAudioStates(q).some(isQueueable);
        },

        /**
         * 「依序試聽」那顆鍵要顯示哪一種外觀。
         *
         * ⚠⚠ **能不能按，看的是「有沒有任何一句可播」，不是整題的嚴重度。**
         * 3 句就緒 ＋ 2 句產生中，規格 17 說跳過產生中的那兩句照播——
         * 所以這種情況要回可播的狀態，回 `BUSY` 會把整顆鍵鎖住。
         *
         * ⚠ 只要**有任何一句播不到**（待更新、產生中、失敗）就回 `STALE`，
         * 讓驚嘆號角標留著。實心 ▶ 的意思是「按下去會聽到完整的一題」，
         * 有句子會被跳過卻給實心 ▶，老師會以為自己聽完了。
         */
        sequenceState(q) {
            const states = this.questAudioStates(q);
            if (!states.some(isQueueable)) {
                /// 一句都播不了：照嚴重度顯示為什麼。
                if (states.indexOf(AUDIO.FAILED) !== -1) { return AUDIO.FAILED; }
                if (states.indexOf(AUDIO.BUSY) !== -1) { return AUDIO.BUSY; }
                /// ⚠ 整題都沒有文字時回 `NONE`，鍵整顆不顯示（規格 11）。
                return states.some((x) => x !== AUDIO.NONE) ? AUDIO.MISSING : AUDIO.NONE;
            }
            /// ⚠⚠ **`BUSY` 也要算進來。** 3 句就緒＋2 句產生中的時候，按下去會
            /// **默默跳過那 2 句**（規格 17）。這時給乾淨的實心 ▶ 等於騙人——
            /// 老師會以為自己聽完了整題。只有**每一句都播得到**才回 `READY`。
            const skipped = states.some((x) => x !== AUDIO.READY && x !== AUDIO.NONE);
            return skipped ? AUDIO.STALE : AUDIO.READY;
        },

        /**
         * 規格 21：整棵樹還有沒有「非就緒」的句子——有的話存檔鍵要顯示
         * 「儲存並產生音檔」。往上丟給 `UnitQuestEdit`，那顆鍵在它身上。
         *
         * ⚠ `MISSING`（有文字、沒產生過）也算非就緒：老師第一次打完字還沒存檔時，
         * 鍵就該顯示「儲存並產生音檔」。
         */
        reportAudioPending() {
            let pending = 0;
            for (const q of ((this.tree && this.tree.quests) || [])) {
                for (const state of this.questAudioStates(q)) {
                    if (state === AUDIO.STALE || state === AUDIO.MISSING
                        || state === AUDIO.FAILED) {
                        pending += 1;
                    }
                }
            }
            this.$emit('audio-pending', pending);
        },

        measureTrack() {
            this.trackWidth = this.$refs.track ? this.$refs.track.clientWidth : 0;
        },
        onTime(seconds) {
            this.currentTime = seconds;
        },
        onDuration(seconds) {
            this.duration = seconds;
            this.$nextTick(this.measureTrack);
        },
        stopNumber(stop) {
            return this.stops.indexOf(stop) + 1;
        },
        questNumber(q) {
            return this.questNumbers.get(q) || 0;
        },
        isStopActive(stop) {
            return this.hoverTime === stop.time || this.expandedTime === stop.time;
        },
        isDragging(stop) {
            return !!(this.drag && this.drag.time === stop.time);
        },
        focusStop(stop) {
            this.expandedTime = stop.time;
            this.seekAndPause(stop.time);
        },
        toggleStop(stop) {
            this.expandedTime = this.expandedTime === stop.time ? null : stop.time;
            if (this.expandedTime === null) {
                this.expandedQuest = null;
            }
        },
        toggleQuest(q) {
            this.expandedQuest = this.expandedQuest === q ? null : q;
        },
        /** ⑱ 點時間就跳到該時刻並暫停，取代「跳到這一刻確認」按鈕。 */
        seekAndPause(time) {
            if (time === TIME_END || !this.$refs.player) {
                return;
            }
            this.$refs.player.seek(time);
            this.$refs.player.pause();
        },
        /** ⑰ 點膠囊：跳過去、暫停、並就地變成輸入框。 */
        tapTime(stop) {
            this.seekAndPause(stop.time);
            if (stop.time === TIME_END) {
                /// 「播完後」沒有 mm:ss 可編輯，改時間走 ⋯ 選單
                return;
            }
            this.editingTime = stop.time;
            this.timeText = clock(stop.time);
            this.$nextTick(() => {
                const el = Array.isArray(this.$refs.timeInput)
                    ? this.$refs.timeInput[0] : this.$refs.timeInput;
                if (el) {
                    el.focus();
                    el.select();
                }
            });
        },
        /** ⑲ 上下方向鍵 ±1 秒。 */
        nudgeTime(delta) {
            const now = parseClock(this.timeText, this.duration);
            this.timeText = clock(Math.max(0, (now === null ? 0 : now) + delta));
        },
        commitTime(stop) {
            if (this.editingTime === null) {
                return;
            }
            const next = parseClock(this.timeText, this.duration);
            this.editingTime = null;
            /// ⚠ 看不懂就維持原值，不要把停點移到 0 秒
            if (next !== null && next !== stop.time) {
                this.setStopTime(stop, next);
            }
        },
        /**
         * 改一個停點的時間。
         *
         * ⚠⚠ **停點裡的每一題都要改**——停點就是「`time` 相同的那一組」，
         * 只改一題等於把它拆出去變成新的停點。
         *
         * ⚠ 目標時間已經有停點時，兩者會**合併**成一個。那是 `groupStops` 的
         * 自然結果，不是錯誤——老師把兩個停點拖到同一秒就是想合併。
         */
        setStopTime(stop, time) {
            const t = time === TIME_END ? TIME_END : Math.max(0, Math.floor(time));
            stop.quests.forEach((q) => {
                this.$set(q, 'time', t);
            });
            if (this.expandedTime === stop.time) {
                this.expandedTime = t;
            }
        },
        /// ⑨ 拖曳標記
        startDrag(event, stop) {
            this.drag = {
                time: stop.time, quests: stop.quests.slice(),
                startX: event.clientX, moved: false,
            };
            this.onDragMove = this.handleDragMove;
            this.onDragUp = this.handleDragUp;
            document.addEventListener('mousemove', this.onDragMove);
            document.addEventListener('mouseup', this.onDragUp);
        },
        handleDragMove(event) {
            if (!this.drag || !this.$refs.track || !this.duration) {
                return;
            }
            if (!this.drag.moved
                && Math.abs(event.clientX - this.drag.startX) < DRAG_SLOP) {
                return;
            }
            this.drag.moved = true;
            const rect = this.$refs.track.getBoundingClientRect();
            const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
            const t = Math.round(ratio * this.duration);
            if (t === this.drag.time) {
                return;
            }
            this.drag.quests.forEach((q) => {
                this.$set(q, 'time', t);
            });
            if (this.expandedTime === this.drag.time) {
                this.expandedTime = t;
            }
            this.drag.time = t;
            /// 拖到哪就跳到哪，老師看得到那一幀
            this.seekAndPause(t);
        },
        handleDragUp() {
            this.stopDrag();
        },
        stopDrag() {
            if (this.onDragMove) {
                document.removeEventListener('mousemove', this.onDragMove);
                document.removeEventListener('mouseup', this.onDragUp);
                this.onDragMove = null;
                this.onDragUp = null;
            }
            this.drag = null;
        },
        /// 點軌道空白處：跳到那個位置
        onTrackDown(event) {
            if (!this.$refs.track || !this.duration) {
                return;
            }
            const rect = this.$refs.track.getBoundingClientRect();
            const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
            this.seekAndPause(Math.round(ratio * this.duration));
        },

        /// ①②③④ 新增：不問對話框，直接建在目前這一秒並展開
        addHere() {
            if (this.$refs.player) {
                /// ② 凍結在當下那一幀——老師是看著畫面決定「就在這裡問」
                this.$refs.player.pause();
            }
            const time = this.videoUrl ? this.currentTime : TIME_END;
            const stop = this.stops.find((s) => s.time === time);
            if (stop && stop.quests.length >= MAX_QUEST_PER_STOP) {
                return;
            }
            const q = this.blankQuest(time);
            if (stop) {
                /// ⚠ 插在該停點**最後一題之後**，不是陣列末端——停點內的順序就是作答順序
                const last = stop.quests[stop.quests.length - 1];
                this.subQuests.splice(this.subQuests.indexOf(last) + 1, 0, q);
            } else {
                this.subQuests.push(q);
            }
            this.expandedTime = time;
            this.expandedQuest = q;
        },
        addQuestToStop(stop) {
            if (stop.quests.length >= MAX_QUEST_PER_STOP) {
                return;
            }
            const q = this.blankQuest(stop.time);
            const last = stop.quests[stop.quests.length - 1];
            this.subQuests.splice(this.subQuests.indexOf(last) + 1, 0, q);
            this.expandedTime = stop.time;
            this.expandedQuest = q;
        },
        blankQuest(time) {
            return {
                time,
                /// ⚠ 只開放單選題型（規格決策 #4）
                type: 'sa',
                quest_title: { title: '' },
                options: [this.blankOption(1), this.blankOption(2)],
                answers: {},
            };
        },
        isLocked(q) {
            return this.lockedTypes.includes(q.type);
        },
        stopLocked(stop) {
            return stop.quests.some((q) => this.isLocked(q));
        },
        removeStop(stop) {
            if (this.stopLocked(stop)) {
                return;
            }
            stop.quests.forEach((q) => {
                const i = this.subQuests.indexOf(q);
                if (i !== -1) {
                    this.subQuests.splice(i, 1);
                }
            });
            if (this.expandedTime === stop.time) {
                this.expandedTime = null;
                this.expandedQuest = null;
            }
        },
        removeQuest(q) {
            if (this.isLocked(q)) {
                return;
            }
            const i = this.subQuests.indexOf(q);
            if (i !== -1) {
                this.subQuests.splice(i, 1);
            }
            if (this.expandedQuest === q) {
                this.expandedQuest = null;
            }
        },
        /**
         * ⑦ 調整停點內的作答順序。
         *
         * ⚠⚠ 動的是 `subQuests`（扁平陣列）裡的位置，因為**那就是學生端的順序**。
         * `stop.quests` 只是分組的結果，改它不會影響存進去的資料。
         */
        moveQuest(stop, index, delta) {
            const target = index + delta;
            if (target < 0 || target >= stop.quests.length) {
                return;
            }
            const a = this.subQuests.indexOf(stop.quests[index]);
            const b = this.subQuests.indexOf(stop.quests[target]);
            if (a === -1 || b === -1) {
                return;
            }
            const moved = this.subQuests.splice(a, 1)[0];
            this.subQuests.splice(b, 0, moved);
        },

        summaryOf(q) {
            return (q.quest_title && q.quest_title.title) || this.$t('unit.vq.untitled');
        },
        counterFor(text) {
            return String(text || '').length >= MAX_TEXT * COUNTER_AT ? MAX_TEXT : false;
        },
        mediaOf(id) {
            return id ? (this.mediaMap[String(id)] || null) : null;
        },
        /**
         * ⚠⚠ 只讀 `*_media_id`，不讀既有的裸網址 key。編輯器只服務**新建**的題目
         * （需求方 2026-09-11：既有資料不改，也不會用編輯器打開）。
         * **不要「順手補上」裸網址的 fallback**——那是為一個不存在的情境寫的程式碼。
         */
        urlOf(slot, idKey) {
            const media = this.mediaOf(slot && slot[idKey]);
            /// ⚠ 已下架的素材照樣顯示——老師得看得到才知道要換掉哪一格（規格 §4.3）
            return (media && media.url) ? media.url : '';
        },
        openPicker(target) {
            this.pickerTarget = target;
            this.pickerOpen = true;
        },
        /**
         * ⚠ 也要 emit `media-loaded`：`mediaMap` 是**載入時的快照**，剛選的素材不在
         * 裡面。不補進去的話老師會看到「已選好」但預覽是空的。
         */
        onPicked(media) {
            if (!media || !media.media_id || !this.pickerTarget) {
                return;
            }
            this.$emit('media-loaded', media);
            const t = this.pickerTarget;
            if (t.option === undefined) {
                this.$set(t.quest, 'cover_media_id', media.media_id);
            } else if (t.quest.options[t.option]) {
                this.$set(t.quest.options[t.option], 'img_media_id', media.media_id);
            }
        },
        clearCover(q) {
            this.$delete(q, 'cover_media_id');
        },
        clearOptionImg(q, j) {
            if (q.options[j]) {
                this.$delete(q.options[j], 'img_media_id');
            }
        },
        /**
         * 新選項的 id：「這個小題**用過**的最大值 + 1」，刪掉的不回收（決策 D4）。
         *
         * ⚠⚠ 水位要跨整個編輯階段記住。只算現有的話，刪掉 id 3 之後新增的又會拿到 3
         * ——而後端對「前端送了 id」是**沿用**不是重配，那個回收會真的存進資料庫，
         * 學生先前作答的紀錄就對到語意不同的選項。
         */
        nextOptionId(q) {
            const next = this.bumpWatermark(q) + 1;
            this.optionWatermarks.set(q, next);
            return next;
        },
        /** ⚠ 新增與刪除都要呼叫——只在新增時抬的話「刪掉再新增」會把號碼發回去。 */
        bumpWatermark(q) {
            let max = this.optionWatermarks.get(q) || 0;
            (q.options || []).forEach((o) => {
                const n = parseInt(String(o.id), 10);
                if (Number.isInteger(n) && n > max) {
                    max = n;
                }
            });
            this.optionWatermarks.set(q, max);
            return max;
        },
        blankOption(id) {
            return { id: String(id), title: '' };
        },
        addOption(q) {
            if ((q.options || []).length < MAX_OPTION) {
                q.options.push(this.blankOption(this.nextOptionId(q)));
            }
        },
        /** ⚠⚠ 刪選項要連它的 answers 條目一起刪，否則留下永遠到不了的孤兒。 */
        removeOption(q, j) {
            if (!q.options[j]) {
                return;
            }
            this.bumpWatermark(q);
            const id = String(q.options[j].id);
            q.options.splice(j, 1);
            if (q.answers) {
                this.$delete(q.answers, id);
            }
        },
        answerOf(q, opt) {
            return (q && q.answers && opt) ? (q.answers[String(opt.id)] || null) : null;
        },
        isCorrect(q, opt) {
            const a = this.answerOf(q, opt);
            return !!(a && a.is_correct);
        },
        correctCount(q) {
            return (q.options || []).filter((o) => this.isCorrect(q, o)).length;
        },
        responseOf(q, opt) {
            const a = this.answerOf(q, opt);
            return (a && a.response_text) || '';
        },
        ensureAnswer(q, opt) {
            const id = String(opt.id);
            if (!q.answers) {
                this.$set(q, 'answers', {});
            }
            if (!q.answers[id]) {
                this.$set(q.answers, id, { is_correct: false });
            }
            return q.answers[id];
        },
        /**
         * ⚠⚠ 不做單選／複選的區分（需求方 2026-09-14）：勾幾個都可以，只要至少一個。
         * 既有資料 283/602 的小題本來就有多個正解——做成 radio 會擋掉近半數的既有用法。
         */
        setCorrect(q, opt, value) {
            this.$set(this.ensureAnswer(q, opt), 'is_correct', !!value);
        },
        setResponse(q, opt, text) {
            this.$set(this.ensureAnswer(q, opt), 'response_text', text);
        },
        showResponse(q, opt) {
            return !!this.responseOpen[`${this.questNumber(q)}:${opt.id}`]
                || !!this.responseOf(q, opt);
        },
        openResponse(q, opt) {
            this.$set(this.responseOpen, `${this.questNumber(q)}:${opt.id}`, true);
        },
    },
};
</script>

<style scoped>
.vq { padding: 16px; }
.vq-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.vq-title { font-weight: 600; font-size: 15px; }
.vq-sub { font-size: 12px; color: #78909c; margin-top: 2px; }
.vq-note { margin-top: 12px; margin-bottom: 0; }
.vq-player { margin-top: 12px; }

/* ⑯ 中文標籤一律不換行，否則窄欄會把它折成豎排 */
.nowrap { white-space: nowrap; }

/* 時間軸 */
.vq-track-wrap { margin-top: 10px; }
.vq-track { position: relative; height: 26px; cursor: pointer; }
.vq-track-bar {
    position: absolute; top: 10px; left: 0; right: 0; height: 6px;
    background: #eceff1; border-radius: 3px;
}
.vq-track-played {
    position: absolute; top: 0; bottom: 0; left: 0; background: #b0bec5; border-radius: 3px;
}
.vq-mk-wrap { position: absolute; top: 0; transform: translateX(-50%); }
/* ⑩ 平常是 24px 圓點，只顯示編號 */
.vq-mk {
    position: relative; width: 24px; height: 24px; border-radius: 50%;
    background: #fff; border: 2px solid #F4622B; color: #F4622B;
    font-size: 11px; line-height: 20px; padding: 0; cursor: grab;
    display: flex; align-items: center; justify-content: center;
}
.vq-mk--drag { cursor: grabbing; }
.vq-mk--group { border-style: dashed; cursor: pointer; }
/* ⑫ hover 或選中時展開成膠囊並提到最上層 */
.vq-mk--on {
    width: auto; border-radius: 12px; padding: 0 8px; background: #F4622B; color: #fff;
}
.vq-mk-wrap:hover, .vq-mk-wrap:focus-within { z-index: 3; }
.vq-mk-cap { font-size: 11px; }
/* ⑪ 多題的角標 */
.vq-mk-badge {
    position: absolute; top: -5px; right: -5px; min-width: 14px; height: 14px;
    border-radius: 7px; background: #455a64; color: #fff; font-size: 9px; line-height: 14px;
    padding: 0 3px;
}
.vq-track-foot {
    display: flex; justify-content: space-between; gap: 12px;
    font-size: 11px; color: #90a4ae; margin-top: 2px;
}

/* 停點卡片 */
.vqs { margin-top: 12px; }
.vqs--on { border-color: #F4622B; }
.vqs-head {
    display: flex; align-items: center; gap: 8px; padding: 8px 12px; cursor: pointer;
}
.vqs-no {
    flex: 0 0 auto; width: 22px; height: 22px; border-radius: 50%; background: #eceff1;
    color: #546e7a; font-size: 12px; line-height: 22px; text-align: center;
}
.vq-cap {
    flex: 0 0 auto; font-variant-numeric: tabular-nums; font-size: 12px; color: #546e7a;
    background: #eceff1; border: 0; border-radius: 10px; padding: 1px 8px; cursor: pointer;
}
.vq-cap--edit { width: 64px; background: #fff; border: 1px solid #F4622B; cursor: text; }
.vqs-meta { font-size: 12px; color: #78909c; }
.vqs-warn { font-size: 11px; color: #ef6c00; }
.vqs-body { padding: 0 12px 12px; }
.vqs-hint { font-size: 12px; color: #90a4ae; }
.vqs-order-title { font-size: 12px; color: #78909c; margin: 10px 0 4px; }
.vqs-add {
    display: flex; align-items: center; gap: 8px;
    border-top: 1px solid #eceff1; padding-top: 6px; margin-top: 2px;
}
.vqs-full { font-size: 11px; color: #90a4ae; }

/* 題目列 */
.vqq { border-top: 1px solid #eceff1; }
.vqq-row { display: flex; align-items: center; gap: 8px; padding: 6px 0; }
.vqq-no {
    flex: 0 0 auto; width: 20px; height: 20px; border-radius: 50%; background: #F4622B;
    color: #fff; font-size: 11px; line-height: 20px; text-align: center;
}
.vqq-summary {
    flex: 1 1 auto; min-width: 0; font-size: 13px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.vqq-meta { flex: 0 0 auto; font-size: 11px; color: #90a4ae; }
.vqq-edit { padding: 4px 0 12px; }
.vqq-foot { display: flex; align-items: center; margin-top: 6px; }

/* 題目內容 */
.vqc-q { display: flex; gap: 16px; align-items: flex-start; }
.vqc-q-text { flex: 1 1 auto; min-width: 0; }
/**
 * ⚠ 縮圖從 64px 變 96px（16:9）之後欄寬要跟著加，否則側欄的「替換」會被擠出去。
 * 176 → 208 正好補回那 32px，維持原本的餘裕。
 * ⚠ 算的是**英文**的 "Replace"（比中文「替換」寬）——按最寬的語言算，
 * 不然 i18n 切過去才發現版面破掉。
 */
.vqc-cover { flex: 0 0 208px; }
.vqc-field-label { font-size: 12px; color: #78909c; margin-bottom: 4px; }
.vqc-thumbwrap { display: flex; align-items: flex-start; gap: 8px; }
.vqc-thumb-side { display: flex; align-items: center; gap: 2px; }
.vqc-thumb { flex: 0 0 auto; border-radius: 4px; overflow: hidden; background: #fafbfc; }
.vqc-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
/**
 * ⚠⚠ **小題背景圖是 16:9，不是正方形**（2026-09-21 對照學生端與實際素材）。
 *
 * 學生端 `DiaryVideo.vue` 把它當**滿版背景**畫（`background-size: cover`，寬度 100%、
 * 高度隨題幹與選項多寡跑），實際在用的素材是 1080×608 與 1920×1080——都是 16:9。
 * 之前這裡是 64×64，老師看到的是那張寬圖**被裁掉左右、只留中間正方形**的樣子，
 * 跟學生看到的完全不同，選圖時很容易挑錯。
 *
 * ⚠ 學生端那個容器**沒有固定比例**（高度由內容決定），所以沒有一個「正確」的數字。
 * 取 16:9 是因為它對齊實際素材與「偏寬」這個方向，比正方形誠實得多。
 * ⚠ 96px 是配合 `.vqc-cover` 的欄寬（96 ＋ gap 8 ＋ 側欄，欄寬已同步加到 208px）。
 */
.vqc-thumb--64 { width: 96px; height: 54px; }
/**
 * ⚠ 選項圖在學生端是 `rounded-full`（`h-20 w-20` 的**圓形**），
 * 所以這裡也要圓——之前是 4px 圓角方形，四個角在學生端會被切掉而編輯器看不出來。
 * 比例本來就對（兩邊都是 1:1，實際素材 485×485）。
 */
.vqc-thumb--48 { width: 48px; height: 48px; }
.vqc-thumb--round { border-radius: 50%; }
.vqc-thumb--empty {
    border: 1px dashed #cfd8dc; display: flex; align-items: center; justify-content: center;
}
.vqc-noimg { font-size: 10px; color: #b0bec5; }
.vqc-fname {
    font-size: 11px; color: #90a4ae; margin-top: 4px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.vqc-optbar {
    display: flex; gap: 12px; align-items: baseline; margin: 14px 0 6px;
    font-size: 12px; color: #78909c;
}
.vqc-correct-n { color: #2e7d32; }

/* 選項 */
.vqo {
    display: flex; align-items: center; gap: 10px; padding: 8px 10px; margin-bottom: 6px;
    border: 1px solid #eceff1; border-radius: 4px; background: #fff;
}
.vqo-correct {
    flex: 0 0 auto; display: flex; align-items: center; gap: 4px;
    font-size: 12px; color: #90a4ae; cursor: pointer;
}
.vqo-correct--on { color: #2e7d32; font-weight: 500; }
.vqo-correct input { accent-color: #2e7d32; }
.vqo-thumbwrap { flex: 0 0 auto; position: relative; }
.vqo-dots {
    position: absolute; right: -6px; bottom: -6px; background: #fff; border: 1px solid #eceff1;
}
.vqo-fields { flex: 1 1 auto; min-width: 0; }
.vqo-addresp { margin-top: 4px; }
/* 回饋內縮一格，空出來的地方放它的標籤 */
.vqo-resp-row { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.vqo-resp-label { flex: 0 0 auto; width: 36px; font-size: 12px; color: #90a4ae; }
.vqo-resp { flex: 1 1 auto; min-width: 0; }
.vqo-resp >>> input { font-size: 13px; }

/* ─────────────────────────────────────────────────────────────────────
 * VF8 音檔（需求方 2026-09-17 規格）
 * ───────────────────────────────────────────────────────────────────── */

/* 規格 1／2：播放鍵貼著它朗讀的那段文字，與欄位基線對齊。
   ⚠ `align-items: flex-start` 不行——欄位有 counter 時會比鍵高，
   置頂會讓鍵浮在文字上方；用 center 對齊輸入框本身。 */
.vqa-row {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
}
.vqa-row > .v-input { flex: 1 1 auto; min-width: 0; }

/* 規格 19／20：小標底色隨嚴重度變化，讓老師掃視就能找到沒弄好的題目 */
.vqa-tag {
    flex: 0 0 auto;
    padding: 1px 7px;
    border-radius: 9px;
    font-size: 11px;
    line-height: 16px;
    font-weight: 600;
}
.vqa-tag--ready  { background: #e8f3ea; color: #2b6b3a; }
.vqa-tag--stale  { background: #fdf0dc; color: #8a5a10; }
.vqa-tag--busy   { background: #e7eef8; color: #2c5a92; }
.vqa-tag--failed { background: #fbe6e2; color: #a5342a; }

/* 規格 6：正在播放的選項列整列高亮 */
.vqo--playing {
    background: #eef5fd;
    box-shadow: inset 3px 0 0 #1976d2;
}
</style>
